// src/components/styled/FormComponents.js
import styled, { keyframes } from 'styled-components';

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export const FormHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(4)};
  animation: ${slideIn} 0.3s ease;

  h2 {
    margin: 0;
    color: ${props => props.theme.colors.primary};
    font-size: ${props => props.theme.typography.fontSize.large};
  }
`;

export const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.textPrimary};
  padding: ${props => props.theme.spacing(1)};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};

  &:hover {
    color: ${props => props.theme.colors.primary};
    transform: translateX(-2px);
  }

  svg {
    font-size: 1.2rem;
  }
`;

export const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing(4)};
  animation: ${fadeIn} 0.3s ease;

  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing(1)};
  }
`;

export const FormSection = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(4)};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: ${props => props.theme.spacing(2)};
  animation: ${fadeIn} 0.3s ease;

  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing(3)};
  }

  h3{margin-bottom: 15px;}
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing(1)};
`;

export const FormField = styled.div`
  margin-bottom: ${props => props.theme.spacing(2)};
  animation: ${slideIn} 0.3s ease;
  animation-delay: ${props => props.index * 0.05}s;
  opacity: 0;
  animation-fill-mode: forwards;
`;

export const FieldLabel = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing(1)};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.small};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`;

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  border: 1px solid ${props => props.theme.colors.inputGray};
  transition: all 0.2s ease;

  &:focus-within {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.primary}22;
  }

  svg {
    margin-left: ${props => props.theme.spacing(2)};
    color: ${props => props.theme.colors.primary};
  }

  input, select {
    width: 100%;
    padding: ${props => props.theme.spacing(2)};
    padding-left: ${props => props.theme.spacing(1)};
    background: none;
    border: none;
    color: ${props => props.theme.colors.textPrimary};
    font-size: ${props => props.theme.typography.fontSize.medium};

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: ${props => props.theme.colors.textSecondary};
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }
`;

export const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  font-size: ${props => props.theme.typography.fontSize.small};
  margin-top: ${props => props.theme.spacing(1)};
  animation: ${slideIn} 0.3s ease;
`;

export const SubmitButton = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(4)};
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.black};
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  font-size: ${props => props.theme.typography.fontSize.medium};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing(2)};
  margin-top: ${props => props.theme.spacing(4)};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  svg {
    font-size: 1.2rem;
  }

  .icon-spin {
    animation: ${spin} 1s linear infinite;
  }
`;